<template>
  <div :class="{ 'audio-player': isAudioFile }" class="interactive-video-exam-player assignment-video-player">
    <div :class="{ 'audio-container': isAudioFile }" class="video-container">
      <!-- Loading indicator -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p>{{ isAudioFile ? 'Ses dosyası yükleniyor...' : 'Video yükleniyor...' }}</p>
        </div>
      </div>

      <!-- Audio File Visual Design -->
      <div v-if="isAudioFile" class="audio-visual-container">
        <div class="audio-artwork">
          <div class="audio-icon-wrapper">
            <i class="icon-feather-volume-2 audio-main-icon"></i>
          </div>
          <div class="audio-info">
            <h3 class="audio-title">{{ selected_content.title }}</h3>
            <p class="audio-type">Ses Dosyası</p>
          </div>
        </div>
        <div class="audio-visualizer">
          <div class="audio-bars">
            <div v-for="i in 20" :key="i" :style="{ animationDelay: (i * 0.1) + 's' }" class="bar"></div>
          </div>
        </div>
      </div>

      <!-- Time display -->
      <div :class="{ 'audio-time-display': isAudioFile }" class="time-display">
        <div class="current_time">{{ formatTime(currentVideoTime) }}</div>
        <div class="total_time">{{ formatTime(totalDuration) }}</div>
      </div>

      <!-- Seek Status Indicator -->
      <div v-if="!isAudioFile && getSeekStatusText()" :class="seekStatusClass" class="seek-status-indicator">
        {{ getSeekStatusText() }}
      </div>

      <!-- Video/Audio element -->
      <video
          id="interactive-video-player"
          :class="{ 'audio-player-element': isAudioFile }"
          :height="isAudioFile ? '80' : '400'"
          class="video-js vjs-default-skin vjs-big-play-centered"
          controls
          data-setup="{}"
          playsinline
          preload="metadata"
          width="100%"
      >
        <p class="vjs-no-js">
          {{ isAudioFile ? 'Ses dosyası' : 'Video' }} oynatmak için JavaScript etkin olmalıdır.
          <a :href="selected_content.file ? selected_content.file.file_link : '#'" target="_blank">
            {{ isAudioFile ? 'Ses dosyasını' : 'Videoyu' }} doğrudan {{ isAudioFile ? 'dinle' : 'izle' }}
          </a>
        </p>
      </video>

    </div>
  </div>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.min.css';
import 'videojs-contrib-hls';
import { GET_ITEM_DETAIL_BY_ID, UPDATE_ITEM_BY_ID } from "@/core/services/store/REST.module";
import {
  getVideoType,
  formatTime,
  adjustVideoSize,
  setupPageVisibility,
  cleanupPageVisibility
} from "@/utils/videojsUtils";
import { showError } from "@/utils/alertUtils";

export default {
  name: "interactive-video-exam-player",
  components: {},
  props: {
    selected_content: {
      type: Object,
      required: true
    },
    items: {
      type: Object,
      required: true
    },
    hashedCode: {
      type: String,
      default: ''
    },
    pauseOnBackground: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      player: null,
      isLoading: false,
      videoRetryCount: 0,
      maxVideoRetryCount: 3,
      videoRetryDelayMs: 1500,
      videoRetryTimer: null,
      pendingRetryPosition: null,
      isRetryingVideo: false,
      shouldResumeAfterRetry: false,
      // MEDIA_ERR_DECODE için özel skip mekanizması
      decodeErrorSkipCount: 0,
      maxDecodeErrorSkip: 3, // Maksimum 3 saniye ileri atlama
      isSkippingCorruption: false,
      // Sınav ile ilgili tüm state'ler kaldırıldı
      updateContentData: {},
      currentDuration: 0, // Kullanıcının kaldığı pozisyon
      shouldSeekBack: false,
      currentVideoTime: 0, // Mevcut video süresi
      totalDuration: 0, // Toplam video süresi
      maxWatchedTime: 0, // En yüksek izlenen süre
      isClampingRestrictedSeek: false,
      hasPermanentlyRestrictedVideoEnded: false,
      lastAnsweredQuestionTime: null, // Son cevaplanan sorunun zamanı
    };
  },
  computed: {
    isAudioFile() {
      return this.selected_content?.file_category?.key_name === 'documents.audio_document';
    },
    isSeekRestricted() {
      return !this.isAudioFile && !this.canVideoFastForward();
    },
    isPermanentlySeekRestricted() {
      return !this.isAudioFile &&
          !this.selected_content.video_can_be_fast_forwarded &&
          !this.selected_content.video_is_completed_it_can_be_fast_forwarded;
    }
  },
  watch: {
    isSeekRestricted() {
      this.$nextTick(() => {
        this.updateRestrictedSeekArea();
      });
    }
  },
  mounted() {
    this.disableEduLock()
    this.$nextTick(() => {
      this.initVideoPlayer();

      if (this.pauseOnBackground) {
        setupPageVisibility(this.player, this);
      }
    });
  },
  beforeDestroy() {
    this.clearVideoRetryTimer();
    this.resetDecodeErrorState();
    this.removeRestrictedSeekArea();

    if (this.player) {
      this.player.dispose();
    }

    if (this.pauseOnBackground) {
      cleanupPageVisibility(this);
    }
  },
  methods: {
    formatTime,
    disableEduLock() {
      // window a set edilen bu Fn'ler , video ya müdahaale ediyor, diğer video componentlerinde dikkat edilmeli
      const global = window;

      if (typeof global.edulock === 'function') {
        // Yeni, etkisizleştirilmiş versiyon
        global.edulock = () => {
          console.warn('[EduLock] Engellendi: interactive video sırasında devre dışı bırakıldı.');
        };
      }
    },

    initVideoPlayer() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      this.player = videojs('interactive-video-player', {
        controls: true,
        fluid: true,
        preload: 'metadata',
        playsinline: true,//ios da kendi kodlarımızın ekin olması için kulanılır
        aspectRatio: '16:9',
        language: 'tr',
        controlBar: {
          pictureInPictureToggle: false,
          playbackRateMenuButton: false, // iOS'ta playback rate butonunu gizle
          fullscreenToggle: !isIOS, // iOS'ta fullscreen butonunu gizle
        },
        playbackRates: [1],  // Hız seçenekleri

      }, () => {

        this.setupVideoSource();
        this.setupVideoEvents();
        this.setupRestrictedSeekArea();
      });
    },

    canVideoFastForward() {
      let isVideoCanFastForward = false;
      if (this.selected_content.video_can_be_fast_forwarded) {
        isVideoCanFastForward = true;
        return isVideoCanFastForward
      }
      if (this.isVideoCompleted() && this.selected_content.video_is_completed_it_can_be_fast_forwarded) {
        isVideoCanFastForward = true;
        return isVideoCanFastForward

      }
      return isVideoCanFastForward;
    },

    seekStatusClass() {
      if (this.canVideoFastForward()) {
        return 'seek-allowed';
      }

      // video_is_completed_it_can_be_fast_forwarded: true ama henüz tamamlanmamış
      if (!this.selected_content.video_can_be_fast_forwarded && this.selected_content.video_is_completed_it_can_be_fast_forwarded) {
        return 'seek-restricted';
      }

      // Her iki özellik de false ise her zaman kırmızı
      return 'seek-restricted';
    },

    getSeekStatusText() {
      // Ses dosyası ise kontrol yapma
      if (this.isAudioFile) {
        return '';
      }

      // canSeekForward metodunu kullan
      if (this.canVideoFastForward()) {
        return "";
      }

      // video_is_completed_it_can_be_fast_forwarded: true ama henüz tamamlanmamış
      if (!this.selected_content.video_can_be_fast_forwarded && this.selected_content.video_is_completed_it_can_be_fast_forwarded) {
        return this.$t('general.fast_forward_video_after_completed')
      }

      // Her iki özellik de false ise hiçbir zaman ileri alınamaz
      return this.$t('general.cannot_fast_forward_video')
    },

    isVideoCompleted() {
      return this.items.unlocked_items.includes(this.selected_content.id);
    },

    setupVideoSource() {
      if (!this.selected_content?.file?.file_link) {
        console.warn('Video kaynağı bulunamadı');
        return;
      }

      const videoUrl = this.selected_content.file.file_link;

      this.player.src({
        src: videoUrl,
        type: getVideoType(videoUrl)
      });

      this.player.load();
    },

    setupVideoEvents() {
      this.player.on('loadstart', () => {
        this.isLoading = true;
        // Video player'a loading class'ı ekle
        if (this.player && this.player.el()) {
          this.player.el().classList.add('vjs-loading');
        }

      });

      this.player.on('loadedmetadata', () => {
        // Toplam video süresini al
        this.totalDuration = this.player.duration();

        // Video boyutunu aspect ratio'ya göre ayarla
        adjustVideoSize(this.player, this.$el, 'assignment-video');

        if (this.isRetryingVideo && this.pendingRetryPosition !== null) {
          return;
        }

        // Video metadata yüklendiğinde kullanıcının kaldığı yeri al
        this.getVideoWhereIStay(this.selected_content.id);
      });

      this.player.on('canplay', () => {
        this.resetVideoRetryState();
        this.resetDecodeErrorState();
      });

      this.player.on('loadeddata', () => {
        // Video verisi yüklendiğinde pozisyonu ayarla
        if (this.pendingRetryPosition !== null) {
          this.setVideoPosition(this.pendingRetryPosition);
          this.finishVideoRetryRestore();
          return;
        }

        if (this.currentDuration > 0) {
          this.setVideoPosition(this.currentDuration);
        }
      });

      this.player.on('error', (error) => {
        // Skip işlemi sırasında tetiklenen error'ları görmezden gel
        if (this.isSkippingCorruption) {
          return;
        }

        this.isLoading = false;
        this.handleVideoError(error);
      });

      this.player.on('pause', () => {
        // Video duraklatıldığında pozisyonu güncelle
        this.updateVideoWhereStay();
      });

      this.player.on('play', () => {
        if (this.isPermanentlySeekRestricted && this.hasPermanentlyRestrictedVideoEnded) {
          this.startPermanentlyRestrictedReplay(this.player.currentTime());
        }
      });

      this.player.on('ended', () => {
        if (this.isPermanentlySeekRestricted) {
          this.hasPermanentlyRestrictedVideoEnded = true;
        }

        // Video bittiğinde complete-content API'sine istek
        this.$emit('complete-content', this.selected_content);
      });

      // Kısıtlı videolarda kullanıcı yalnızca daha önce izlediği alanda gezinebilir.
      this.player.on('seeking', () => {
        this.enforceRestrictedSeekLimit();
      });

      //saniyede 3 defa tetiklenir
      this.player.on('timeupdate', () => {
        this.currentVideoTime = this.player.currentTime();

        // Kısıtlı videolarda seek sırasında atlanan süre izlenmiş sayılmamalı.
        const canUpdateWatchedTime = !this.isSeekRestricted ||
            (!this.player.seeking() && !this.isClampingRestrictedSeek);

        if (canUpdateWatchedTime && this.currentVideoTime > this.maxWatchedTime) {
          this.maxWatchedTime = this.currentVideoTime;
        }

        this.updateRestrictedSeekArea();
      });
    },

    setupRestrictedSeekArea() {
      if (!this.player) return;

      const progressControl = this.player.controlBar?.getChild('ProgressControl');
      const seekBar = progressControl?.getChild('SeekBar');
      const progressControlElement = progressControl?.el();
      const seekBarElement = seekBar?.el();

      if (!progressControlElement || !seekBarElement || this._restrictedSeekArea) return;

      const restrictedArea = document.createElement('div');
      restrictedArea.className = 'vjs-restricted-seek-area';
      restrictedArea.setAttribute('aria-hidden', 'true');

      const blockRestrictedInteraction = (event) => {
        if (!this.isRestrictedTimelineEvent(event, seekBarElement)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      };

      const interactionEvents = ['pointerdown', 'mousedown', 'click', 'dblclick', 'touchstart'];
      const captureOptions = {capture: true, passive: false};

      // Safari'de Video.js seek işlemi media `seeking` eventinden önce görüntüyü
      // değiştirebildiği için, izlenmemiş alandaki etkileşimi capture aşamasında kes.
      interactionEvents.forEach((eventName) => {
        progressControlElement.addEventListener(eventName, blockRestrictedInteraction, captureOptions);
      });

      // Timeline dışındaki giriş yollarında ve sınırı aşan sürüklemelerde de
      // media currentTime değişmeden önce hedef zamanı sınırla.
      const originalUserSeek = seekBar.userSeek_;
      seekBar.userSeek_ = (requestedTime) => {
        const maxAllowedTime = this.getRestrictedSeekLimit();

        if (this.isSeekRestricted && requestedTime > maxAllowedTime) {
          return;
        }

        if (this.isPermanentlySeekRestricted &&
            this.hasPermanentlyRestrictedVideoEnded &&
            requestedTime < maxAllowedTime) {
          this.startPermanentlyRestrictedReplay(requestedTime);
        }

        return originalUserSeek.call(seekBar, requestedTime);
      };

      seekBarElement.appendChild(restrictedArea);
      this._restrictedSeekArea = restrictedArea;
      this._restrictedProgressControlElement = progressControlElement;
      this._restrictedInteractionHandler = blockRestrictedInteraction;
      this._restrictedInteractionEvents = interactionEvents;
      this._restrictedCaptureOptions = captureOptions;
      this._restrictedSeekBar = seekBar;
      this._originalSeekBarUserSeek = originalUserSeek;
      this.updateRestrictedSeekArea();
    },

    isRestrictedTimelineEvent(event, seekBarElement) {
      if (!this.isSeekRestricted || !seekBarElement) return false;

      if (event.type === 'mousedown' && event.button !== 0) {
        return false;
      }

      // Pointer restricted katmanın üstünde görünürken kendi üzerinden
      // sürüklenebilsin; sınır aşımı userSeek_ tarafından yine engellenir.
      if (event.target?.closest?.('.vjs-play-progress')) {
        return false;
      }

      const pointer = event.touches?.[0] || event.changedTouches?.[0] || event;
      const rect = seekBarElement.getBoundingClientRect();

      if (!rect.width || !isFinite(pointer.clientX)) {
        return false;
      }

      const pointerPercentage = Math.min(1, Math.max(0, (pointer.clientX - rect.left) / rect.width));
      const duration = this.player?.duration();

      if (!duration || !isFinite(duration)) {
        return false;
      }

      return pointerPercentage * duration > this.getRestrictedSeekLimit();
    },

    getRestrictedSeekLimit() {
      const duration = this.player?.duration();

      return duration && isFinite(duration)
          ? Math.min(this.maxWatchedTime, duration)
          : this.maxWatchedTime;
    },

    updateRestrictedSeekArea() {
      if (!this.player || !this._restrictedSeekArea) return;

      if (!this.isSeekRestricted) {
        this._restrictedSeekArea.style.display = 'none';
        return;
      }

      const duration = this.player.duration();
      const seekLimit = this.getRestrictedSeekLimit();
      const watchedPercentage = duration && isFinite(duration)
          ? Math.min(100, Math.max(0, (seekLimit / duration) * 100))
          : 0;

      this._restrictedSeekArea.style.display = '';
      this._restrictedSeekArea.style.left = `${watchedPercentage}%`;
    },

    removeRestrictedSeekArea() {
      if (this._restrictedProgressControlElement && this._restrictedInteractionHandler) {
        (this._restrictedInteractionEvents || []).forEach((eventName) => {
          this._restrictedProgressControlElement.removeEventListener(
              eventName,
              this._restrictedInteractionHandler,
              this._restrictedCaptureOptions
          );
        });
      }

      if (this._restrictedSeekBar && this._originalSeekBarUserSeek) {
        this._restrictedSeekBar.userSeek_ = this._originalSeekBarUserSeek;
      }

      if (this._restrictedSeekArea?.parentNode) {
        this._restrictedSeekArea.parentNode.removeChild(this._restrictedSeekArea);
      }

      this._restrictedSeekArea = null;
      this._restrictedProgressControlElement = null;
      this._restrictedInteractionHandler = null;
      this._restrictedInteractionEvents = null;
      this._restrictedCaptureOptions = null;
      this._restrictedSeekBar = null;
      this._originalSeekBarUserSeek = null;
    },

    startPermanentlyRestrictedReplay(position) {
      if (!this.isPermanentlySeekRestricted) return;

      const duration = this.player?.duration();
      const isValidPosition = Number.isFinite(position) &&
          (!Number.isFinite(duration) || position < duration);
      const replayPosition = isValidPosition ? Math.max(0, position) : 0;

      // Her yeniden izleme kendi izlenen alanını oluşturur. Kullanıcı bu alan
      // içinde ileri-geri gidebilir, henüz yeniden izlemediği alana geçemez.
      this.maxWatchedTime = replayPosition;
      this.currentVideoTime = replayPosition;
      this.hasPermanentlyRestrictedVideoEnded = false;
      this.updateRestrictedSeekArea();
    },

    enforceRestrictedSeekLimit() {
      if (!this.player || !this.isSeekRestricted || this.isSkippingCorruption || this.isClampingRestrictedSeek) {
        return;
      }

      const requestedTime = this.player.currentTime();
      const maxAllowedTime = this.getRestrictedSeekLimit();

      if (this.isPermanentlySeekRestricted &&
          this.hasPermanentlyRestrictedVideoEnded &&
          isFinite(requestedTime) &&
          requestedTime < maxAllowedTime) {
        this.startPermanentlyRestrictedReplay(requestedTime);
        return;
      }

      if (!isFinite(requestedTime) || requestedTime <= maxAllowedTime) {
        return;
      }

      this.isClampingRestrictedSeek = true;
      this.player.currentTime(Math.max(0, maxAllowedTime));
      this.currentVideoTime = Math.max(0, maxAllowedTime);
      this.isClampingRestrictedSeek = false;
    },

    handleVideoError(error) {
      console.log("error", error);

      // Pozisyonu hata vermeden önce kaydet
      this.updateVideoWhereStay();

      this.isLoading = false;
      this.shouldResumeAfterRetry = this.player ? !this.player.paused() : true;

      // Video.js error kodunu al
      const errorCode = this.player?.error()?.code;

      // MEDIA_ERR_DECODE (CODE:3) - Video decode hatası
      if (errorCode === 3) {
        console.warn('MEDIA_ERR_DECODE hatası tespit edildi, corrupt segment atlanmaya çalışılıyor...');
        this.handleDecodeError();
        return;
      }

      // MEDIA_ERR_NETWORK (CODE:2) veya diğer hatalar için retry mekanizması
      const hasRetry = this.retryVideoPlayback();
      if (hasRetry) {
        return;
      }

      console.error('Video oynatılamadı hata:', error);
      this.showFinalError(errorCode);
    },

    retryVideoPlayback() {
      if (!this.player || !this.selected_content?.file?.file_link) {
        return false;
      }

      if (this.videoRetryCount >= this.maxVideoRetryCount) {
        this.clearVideoRetryTimer();
        this.pendingRetryPosition = null;
        this.isRetryingVideo = false;
        return false;
      }

      const currentTime = this.player.currentTime();
      const fallbackTime = this.maxWatchedTime || this.currentDuration || 0;
      this.pendingRetryPosition = currentTime > 0 ? currentTime : fallbackTime;
      this.isRetryingVideo = true;

      this.videoRetryCount += 1;
      this.clearVideoRetryTimer();

      const retryAttempt = this.videoRetryCount;
      this.videoRetryTimer = setTimeout(() => {
        if (!this.player || this.player.isDisposed && this.player.isDisposed()) {
          return;
        }

        this.isLoading = true;
        this.player.error(null);

        // Kaynağı tekrar yükle ve oynatmayı yeniden dene.
        if (this.shouldResumeAfterRetry) {
          this.player.one('seeked', () => {
          if (!this.player || this.player.isDisposed && this.player.isDisposed()) {
            return;
          }

            const playPromise = this.player.play();

            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch((playError) => {
                console.error(`Video oynatma retry denemesi başarısız oldu (${retryAttempt}/${this.maxVideoRetryCount}):`, playError);
              });
            }
          });
        }

        this.setupVideoSource();
      }, this.videoRetryDelayMs);

      return true;
    },

    handleDecodeError() {
      const currentTime = this.player.currentTime();
      const duration = this.player.duration();
      const percentWatched = (currentTime / duration) * 100;

      console.log(`Decode hatası - Pozisyon: ${currentTime.toFixed(2)}s, Süre: ${duration.toFixed(2)}s, İzlenme: %${percentWatched.toFixed(2)}`);

      // Video %95+ izlenmişse tamamlanmış say
      if (percentWatched >= 95) {
        console.warn('Video %95+ izlendi, decode hatası görmezden geliniyor ve video tamamlanmış sayılıyor');
        this.player.error(null);
        this.$emit('complete-content', this.selected_content);
        return;
      }

      // Corrupt segment'i atlamayı dene
      this.attemptSkipCorruption();
    },

    attemptSkipCorruption() {
      if (!this.player || this.player.isDisposed && this.player.isDisposed()) {
        return;
      }

      // Maksimum skip sayısına ulaşıldı mı kontrol et
      if (this.decodeErrorSkipCount >= this.maxDecodeErrorSkip) {
        console.error(`Maksimum ${this.maxDecodeErrorSkip} saniye ileri atlandı, hata hala devam ediyor`);
        this.resetDecodeErrorState();
        this.showFinalError(3);
        return;
      }

      this.decodeErrorSkipCount += 1;
      this.isSkippingCorruption = true;

      const currentTime = this.player.currentTime();
      const newPosition = currentTime + 1; // 1 saniye ileri al
      const duration = this.player.duration();

      console.log(`Corrupt segment atlanıyor: ${currentTime.toFixed(2)}s -> ${newPosition.toFixed(2)}s (Deneme ${this.decodeErrorSkipCount}/${this.maxDecodeErrorSkip})`);

      // Eğer video sonuna geldiysek ve hala hata varsa, tamamlanmış say
      if (newPosition >= duration - 1) {
        console.warn('Video sonuna gelindi, tamamlanmış sayılıyor');
        this.player.error(null);
        this.resetDecodeErrorState();
        this.$emit('complete-content', this.selected_content);
        return;
      }

      // Hatayı temizle
      this.player.error(null);

      // Yeni pozisyona git
      this.player.currentTime(newPosition);

      // maxWatchedTime'ı güncelle (skip edilen bölüm de izlenmiş sayılsın)
      if (newPosition > this.maxWatchedTime) {
        this.maxWatchedTime = newPosition;
      }

      // Kısa bir süre sonra hatanın devam edip etmediğini kontrol et
      setTimeout(() => {
        this.isSkippingCorruption = false; // Skip işlemi bitti, yeni error'ları dinlemeye başla

        if (this.player && !this.player.error()) {
          console.log('Corrupt segment başarıyla atlandı, video devam ediyor');
          this.resetDecodeErrorState();

          // Video oynatılıyorsa devam ettir
          if (this.shouldResumeAfterRetry) {
            const playPromise = this.player.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch((playError) => {
                console.error('Skip sonrası oynatma hatası:', playError);
              });
            }
          }
        }
        // Eğer hala error varsa, error event otomatik tetiklenecek ve handleDecodeError tekrar çağrılacak
      }, 500);
    },

    resetDecodeErrorState() {
      this.decodeErrorSkipCount = 0;
      this.isSkippingCorruption = false;
    },

    showFinalError(errorCode) {
      let errorMessage = this.$t('general.video_play_error');

      if (errorCode === 3) {
        errorMessage = this.$t('general.video_broken_error');
      } else if (errorCode === 2) {
        errorMessage = this.$t('general.internet_connection_gone');
      }

      // Kullanıcıya bilgilendirme göster
      showError(errorMessage, 5000);
      console.error('Video Hatası:', errorMessage);
    },

    resetVideoRetryState() {
      this.videoRetryCount = 0;
      this.clearVideoRetryTimer();
    },

    finishVideoRetryRestore() {
      this.pendingRetryPosition = null;
      this.isRetryingVideo = false;
      this.shouldResumeAfterRetry = false;
    },

    clearVideoRetryTimer() {
      if (this.videoRetryTimer) {
        clearTimeout(this.videoRetryTimer);
        this.videoRetryTimer = null;
      }
    },

    // parent component'ten çağrılabilir  - modal kapatılırsa video positionu guncelle
    updateVideoWhereStay() {
      if (!this.player || !this.selected_content || !this.hashedCode) return false;

      const currentTime = this.player.currentTime();
      const duration = this.player.duration();

      if (!duration || isNaN(duration)) return false;

      // En yüksek izleme süresini kullan (eğer mevcut süre daha büyükse güncelle)
      const maxTime = Math.max(this.maxWatchedTime, currentTime);
      const secondsDuration = Number(
          Math.min(duration, Math.max(0, maxTime)).toFixed(3)
      );
      // Uzun videolarda konum hassasiyetini korurken payload'ı 4 ondalık haneyle sınırla.
      const completionPercentage = Number(
          ((secondsDuration * 100) / duration).toFixed(4)
      );

      this.updateContentData = {
        completion_percentage: completionPercentage,
        playback_position_seconds: secondsDuration,
        hashed: this.hashedCode, // Hash değerini gönder
      };

      // Backend'e gönder
      this.$store.dispatch(UPDATE_ITEM_BY_ID, {
        url: `api/assignment/update-content/${this.$route.params.id}/${this.selected_content.id}`,
        contents: this.updateContentData,
        noLoading: true,
      })
          .then((result) => {
            // Yeni hash'i parent'a emit et
            if (result.status && result.data) {
              this.$emit('update-hash', result.data.hashed);
            }
          })
          .catch((error) => {
            console.error('Error updating video position:', error);
          });
    },

    getVideoWhereIStay(content_id) {
      let self = this;
      let url = `api/assignment/${self.$route.params.id}/content/${content_id}/stay`;

      self.$store.dispatch(GET_ITEM_DETAIL_BY_ID, {
        url: url,
        acceptPromise: false,
      })
          .then((result) => {
            if (result.status) {
              // Stay endpoint'i kaldığı konumu saniye cinsinden döndürür.
              const secondsFromApi = Number(result.data);
              const shouldRestartPermanentlyRestrictedVideo =
                  this.isPermanentlySeekRestricted && this.isVideoCompleted();

              if (shouldRestartPermanentlyRestrictedVideo) {
                // Daha önce tamamlanmış olsa bile "ileri sarılamaz" video her
                // açılışta ilk izleme kurallarıyla baştan izlenir.
                this.currentDuration = 0;
                this.maxWatchedTime = 0;
                this.hasPermanentlyRestrictedVideoEnded = false;
              } else if (Number.isFinite(secondsFromApi) && secondsFromApi >= 0) {
                this.currentDuration = secondsFromApi;
              } else {
                this.currentDuration = 0;
              }

              // Video pozisyonunu ayarla
              this.setVideoPosition(this.currentDuration);
            } else {
              // Eğer pozisyon bulunamazsa loading'i kapat
              this.isLoading = false;
              if (this.player && this.player.el()) {
                this.player.el().classList.remove('vjs-loading');
              }
            }
          })
          .catch((error) => {
            console.error('Video pozisyonu alınamadı:', error);
            this.isLoading = false;
            this.removeBigPlayButton()
          });
    },

    setVideoPosition(position) {
      if (!this.player || !this.player.duration()) {
        // Video henüz hazır değilse, biraz bekleyip tekrar dene
        setTimeout(() => {
          this.setVideoPosition(position);
        }, 500);
        return;
      }

      const duration = this.player.duration();
      const targetPosition = position >= duration ? 0 : position;

      // API'den veya retry mekanizmasından gelen pozisyon, güvenilir bir izleme sınırıdır.
      this.maxWatchedTime = this.isPermanentlySeekRestricted && position >= duration
          ? 0
          : Math.max(this.maxWatchedTime, Math.min(Math.max(0, position), duration));

      // Eğer pozisyon video süresinden büyükse, baştan başlat
      if (position >= duration) {
        this.player.currentTime(0);
        //Video tamamlanmış, baştan başlat
      } else {
        this.player.currentTime(position);
      }

      this.currentDuration = targetPosition;
      this.updateRestrictedSeekArea();

      // Video pozisyonu ayarlandıktan sonra loading'i kapat
      setTimeout(() => {
        this.isLoading = false;
        this.removeBigPlayButton()

        this.updateRestrictedSeekArea();
      }, 600);
    },

    removeBigPlayButton() {
      // Video player'dan loading class'ını kaldır
      if (this.player && this.player.el()) {
        this.player.el().classList.remove('vjs-loading');
      }
    },

  }
}
</script>

<style scoped src="./assignment-video-player.css"/>
